// Browser errors → first-party analytics (event "client_error"), so a broken
// release shows up as numbers per market and per release instead of as a
// user's screenshot. No message text beyond 200 characters, no stack with
// user data, at most a few reports per page load.
import { track } from "@/lib/track";
import { noteError } from "@/lib/sessionTrail";

export type ErrorKind = "error" | "rejection" | "render" | "chunk" | "translate" | "draft_save";

const RELEASE = (import.meta.env.VITE_RELEASE as string | undefined)?.slice(0, 12) || "dev";
const MAX_PER_PAGE = 5;
const seen = new Set<string>();

/** Browser noise that says nothing about our code. */
export const isNoise = (message: string): boolean =>
  /ResizeObserver loop|^Script error\.?$|Non-Error promise rejection captured|extension:\/\/|Lock broken by another request|isAcquireTimeout|Loader\.provide not called by module/i.test(message);

/** The request never reached us (offline, page closed, tab switched): not a bug. */
export const isNetworkError = (message: string): boolean =>
  /Failed to fetch|Load failed|NetworkError|AbortError|network connection was lost|Lock broken by another request/i.test(message);

/** A lazy chunk from an older deploy failed to load (open tab across a release). */
export const isChunkError = (message: string): boolean =>
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(message);

const RELOAD_KEY = "myst_chunk_reload_at";

/**
 * A page whose code failed to load because a release changed the file names
 * (a tab left open across a deploy, or the seconds while Cloudflare rolls a
 * new version out everywhere) loads itself again, once: the fresh page asks
 * for the new files. Returns false when it already tried in the last 30 s,
 * so a real outage shows the reload screen instead of looping.
 */
export function reloadOnceForNewRelease(now: number = Date.now(), reload: () => void = () => window.location.reload()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (now - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Last path segment of a script URL, without the query: where it broke, not who. */
export const sourceOf = (url?: string | null): string | null =>
  url ? url.split("?")[0].split("/").pop()?.slice(0, 80) || null : null;

export function reportError(kind: ErrorKind, message: string, extra: Record<string, unknown> = {}) {
  const msg = String(message || "unknown").slice(0, 200);
  if (isNoise(msg)) return;
  noteError(kind, msg);
  const key = `${kind}:${msg}`;
  if (seen.has(key) || seen.size >= MAX_PER_PAGE) return;
  seen.add(key);
  track("client_error", { kind: isChunkError(msg) ? "chunk" : kind, message: msg, release: RELEASE, ...extra });
}

/** Test hook: forget what was reported on this "page". */
export const resetErrorReporting = () => seen.clear();

export function installErrorReporting() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    reportError("error", e.message, { source: sourceOf(e.filename), line: e.lineno || null });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    reportError("rejection", r instanceof Error ? r.message : typeof r === "string" ? r : "unhandled rejection");
  });
}
